import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Typography,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AssetDetail } from '~/app/services/asset/asset.service';
import { AssetService } from '~/app/services/asset/asset.service';

type Props = {
  asset: AssetDetail;
  organizationChargerAuthEnabled: boolean;
  organizationLoading?: boolean;
};

export function AssetOcppAuthenticationSection({
  asset,
  organizationChargerAuthEnabled,
  organizationLoading = false,
}: Props) {
  const { t } = useTranslation('assets');
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: (nextPassword: string) =>
      AssetService.setOcppCredentials(
        asset.organizationId,
        asset.id,
        nextPassword,
      ),
    onSuccess: () => {
      setPassword('');
      form.resetFields();
      void queryClient.invalidateQueries({
        queryKey: ['assets', 'detail', asset.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ['assets', 'ocpp-settings', asset.id],
      });
      message.success(
        t('detail.ocppAuth.saveSuccess', 'OCPP password saved successfully'),
      );
    },
    onError: () => {
      message.error(
        t('detail.ocppAuth.saveError', 'Failed to save OCPP password'),
      );
    },
  });

  const handleSubmit = async () => {
    const trimmed = password.trim();
    if (trimmed.length < 6) {
      message.error(
        t(
          'detail.ocppAuth.passwordMinLength',
          'Password must be at least 6 characters',
        ),
      );
      return;
    }

    await mutation.mutateAsync(trimmed);
  };

  const isConfigured = asset.chargerAuthenticationConfigured === true;
  const serialNumber = asset.serialNumber?.trim() || '-';

  return (
    <Card
      style={{ borderRadius: 16, border: '1px solid #e2e8f0' }}
      title={t('detail.ocppAuth.title', 'OCPP Authentication')}
      loading={organizationLoading}
    >
      <Flex vertical gap={12}>
        <Typography.Text type="secondary">
          {t(
            'detail.ocppAuth.description',
            'Basic Auth username is the asset serial number (Charge Point ID).',
          )}
        </Typography.Text>

        <Typography.Text>
          {t('detail.fields.serialNumber', 'Serial number')}:{' '}
          <Typography.Text strong copyable={serialNumber !== '-'}>
            {serialNumber}
          </Typography.Text>
        </Typography.Text>

        {!organizationChargerAuthEnabled ? (
          <Alert
            type="info"
            showIcon
            message={t(
              'detail.ocppAuth.orgDisabledTitle',
              'Charger authentication is disabled',
            )}
            description={t(
              'detail.ocppAuth.orgDisabledDescription',
              'Enable "Require charger HTTP Basic Authentication" in the organization OCPP settings to set a charger password.',
            )}
          />
        ) : (
          <>
            <Typography.Text>
              {t('detail.ocppAuth.statusLabel', 'Status')}:{' '}
              <Typography.Text strong>
                {isConfigured
                  ? t('detail.ocppAuth.statusConfigured', 'Configured')
                  : t('detail.ocppAuth.statusNotConfigured', 'Not configured')}
              </Typography.Text>
            </Typography.Text>

            <Form
              form={form}
              layout="vertical"
              onFinish={() => void handleSubmit()}
            >
              <Form.Item
                label={t('detail.ocppAuth.passwordLabel', 'Password')}
                required
              >
                <Input.Password
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t(
                    'detail.ocppAuth.passwordPlaceholder',
                    'Enter charger password',
                  )}
                  autoComplete="new-password"
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={mutation.isPending}
                disabled={!password.trim()}
              >
                {isConfigured
                  ? t('detail.ocppAuth.changePassword', 'Change password')
                  : t('detail.ocppAuth.setPassword', 'Set password')}
              </Button>
            </Form>
          </>
        )}
      </Flex>
    </Card>
  );
}
